import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useStorage } from '@vueuse/core'

export type UserData = Record<string, any> | null

export const useUserSession = defineStore('userSession', () => {
  // token will be synced with local storage
  // @see https://vueuse.org/core/usestorage/

  const nToken = localStorage.getItem('token')

  const token = useStorage('token', nToken)

  const user = ref<Partial<UserData>>()
  const loading = ref(true)

  const isLoggedIn = computed(() => token.value !== undefined && token.value !== '')

  function setUser(newUser: Partial<UserData>) {
    user.value = newUser
  }

  function setToken(newToken: string) {
    token.value = newToken
  }

  function setLoading(newLoading: boolean) {
    loading.value = newLoading
  }

  async function logoutUser() {
    token.value = undefined
    user.value = undefined
  }

  function hasClearence(clearenceCode: string) {
    const clearences: any[] = user.value?.clearence || []
    return clearences.includes(clearenceCode)
  }

  function hasPermissions(permissionsCode: string) {
    const permissions: any[] = user.value?.permissions || []
    return permissions.includes(permissionsCode)
  }

  function hasPermissionsAH(permissionsCode: string) {
    const permissions: any[] = user.value?.permissionsAH || []
    return permissions.includes(permissionsCode)
  }

  function hasPermissionsDatastudioHogar(permissionsCode: string) {
    const permissions: any[] = user.value?.permissionsDatastudioHogar || []
    return permissions.includes(permissionsCode)
  }

  function hasPermissionsHogar(permissionsCode: string) {
    const permissions: any[] = user.value?.permissionsHogar || []
    return permissions.includes(permissionsCode)
  }

  function hasPermissionsReportes(permissionsCode: string) {
    const permissions: any[] = user.value?.permissionsReportes || []
    return permissions.includes(permissionsCode)
  }
  function hasPermissionsSelfManagement(permissionsCode: string) {
    const permissions: any[] = user.value?.permissionsSelfManagement || []
    return permissions.includes(permissionsCode)
  }

  return {
    user,
    token,
    isLoggedIn,
    loading,
    logoutUser,
    setUser,
    setToken,
    setLoading,
    hasClearence,
    hasPermissions,
    hasPermissionsAH,
    hasPermissionsHogar,
    hasPermissionsReportes,
    hasPermissionsDatastudioHogar,
    hasPermissionsSelfManagement
  } as const
})

/**
 * Pinia supports Hot Module replacement so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserSession, import.meta.hot))
}
